import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import Avatar from '@/components/hcp/Avatar/Avatar';
import { AppRoutes } from '@/utils/constants';
import { getInitials } from '@/utils/string';
import { useCurrentPageName } from '@/hooks/useCurrentPageName';
import styles from './UserMenu.module.scss';
import { Link } from 'react-router-dom';
import { TestIds } from './testids';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { useLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useLinkClick';
import { useCentralizedLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useCentralizedLinkClick';
import useSignOut from '@/hooks/AdobeAnalyticsHooks/signOut/useSignOut';
import Popover from '@/storybook/components/Popover/Popover';

interface UserMenuProps {
  onOpen?: () => void;
}

const UserMenu: FC<UserMenuProps> = ({ onOpen }) => {
  const { t } = useTranslation();

  const { logout, userInfo } = useAuthContext();
  const firstName = userInfo?.firstName;
  const lastName = userInfo?.lastName;
  const initials = getInitials({ firstName, lastName });

  const { trackLinkClick } = useLinkClick();
  const { trackTabClick } = useCentralizedLinkClick();
  const { trackSignOut } = useSignOut();
  const currentPageName = useCurrentPageName();

  const handleSignOut = () => {
    trackLinkClick({
      innertext: t('header.userMenu.signOut'),
      position: AdobeAnalyticsConst.POSITIONS.APPLICATION_HEADER,
      type: AdobeAnalyticsConst.LINK_TYPES.BUTTON,
      name: AdobeAnalyticsConst.LINKCLICK_NAMES.SIGN_OUT_BUTTON,
    });
    trackSignOut();
    sessionStorage.setItem('signOutEvent', '1');
    logout();
  };

  const handleAvatarClick = () => {
    trackTabClick({
      innertext: t('header.userMenu.avatar'),
      position: 'APPLICATION_HEADER',
      name: 'USER_MENU_TAB',
    });
  };

  return (
    <Popover
      onActiveFn={onOpen}
      className={styles.userMenu}
      size="S"
      noticePosition="bottom"
      appearance="secondary"
      portal={false}
      closeOnNoticeContentClick
      disableAutoPosition
      withCloseButton={false}
      anchorContent={
        <div
          className={styles.avatarButton}
          data-testid={TestIds.topBar.loginsection}
          onClick={handleAvatarClick}
          role="button"
        >
          <Avatar initials={initials} />
        </div>
      }
      noticeContent={
        <div className={styles.panel}>
          <Avatar className={styles.avatar} initials={initials} />
          <div className={styles.info}>
            <div className={styles.name}>
              {firstName} {lastName}
            </div>
          </div>
          <hr />
          <Link
            className={styles.managePreferencesLink}
            to={AppRoutes.MANAGE_PREFERENCES_USER}
            state={currentPageName ? { from: currentPageName } : undefined}
          >
            {t('header.userMenu.managePreferences')}
          </Link>
          <hr />
          <div onClick={handleSignOut} className={styles.signout}>
            {t('header.userMenu.signOut')}
          </div>
        </div>
      }
    />
  );
};

export default UserMenu;
